import { ref, readonly, h, defineComponent } from "vue";
import NotifyDialogComponent from '@/components/dialogs/NotifyDialog.vue';
import repository from '@/repositories/repositoryFactory';
import { from } from "linq-to-typescript";

// ステート
const modal = ref({
    title: "", // TITLE
    message: "", // メッセージ
    notifyInfo: [] as CompanyType.NotifyInfo2[], // 通知先一覧
    toSent: "", // 送信先
    isSent: false, // 送信済みか？
    isError: false, // 通知送信でエラーがあるか？
    isInvalid: false // バリデーションフラグ
});

// ダイアログオープン制御フラグ
const isOpen = ref(false);

// 現在選択している通知先
const selectedNotifyId = ref(0);

// 認証コード
const authCode = ref("");

// 戻り値
const _resolve = ref<(ans: string | undefined) => void>(() => { })


const defaultTitle = "認証コード送信";
const defaultMessage = "一時コードによる認証後、処理が行えます。";

/**
 * NotifyDialog
 */
export default function useNotifyDialog() {

    /**
     * 確認ダイアログを開く：ボタンが押されたタイミングで、結果を返す
     * @throws API呼出し等のエラー
     * @return Promise<number | undefined>
     */
    const openNotifyDialog = async (title: string = defaultTitle, message: string = defaultMessage) => {

        // 通知先取得API呼出し
        modal.value.notifyInfo = await repository.company.getNotices();

        // 状態のセット
        modal.value.title = title;
        modal.value.message = message;
        modal.value.toSent = "";
        modal.value.isSent = false;
        modal.value.isError = false;
        modal.value.isInvalid = false;
        selectedNotifyId.value = modal.value.notifyInfo.length > 0 ? modal.value.notifyInfo[0].notifyId : 0;
        authCode.value = "";

        // ダイアログオープン
        isOpen.value = true;

        return new Promise<string | undefined>((resolve) => {
            _resolve.value = resolve
        })
    };

    /**
     * ダイアログを閉じる
     */
    const closeDialog = (result: string | undefined) => {
        // 結果を返却
        _resolve.value(result);

        // ステートリセット
        modal.value.notifyInfo = [];
        modal.value.title = "";
        modal.value.message = "";
        modal.value.toSent = "";
        modal.value.isSent = false;
        modal.value.isError = false;
        modal.value.isInvalid = false;
        selectedNotifyId.value = 0;
        authCode.value = "";

        // モーダルを閉じる
        isOpen.value = false;
        _resolve.value = () => { }
    };

    /**
     * 認証コードを検証してダイアログを閉じる
     */
    const closeDialogWithVerify = () => {
        // 認証コードチェック
        const code = parseInt(authCode.value);
        if (isNaN(code)) {
            modal.value.isInvalid = true;
            return;
        }

        // モーダルを閉じる
        closeDialog(authCode.value);
    };

    /**
     * 認証コードを送信する
     */
    const sendNotify = async () => {
        modal.value.isError = false;

        // 選択されたデータ取得
        const notify = from(modal.value.notifyInfo)
            .where(n => n.notifyId == selectedNotifyId.value)
            .firstOrDefault();
        if (!notify) {
            modal.value.isError = true;
            return;
        }

        try {
            // 情報をセット
            modal.value.toSent = notify?.smsOrMail ?? '';
            modal.value.isSent = true;
            // 通知送信
            await repository.auth.sendAuthCode(notify.smsOrMail, notify.notifyTypeCode);
        }
        catch (error) {
            // ログ出力するのみ
            if (error instanceof Error) {
                // Errorの場合
                console.log(error.message);
            }
        }

    };

    // ダイアログコンポーネントの描写
    const render = () => h(NotifyDialogComponent);
    const NotifyDialog = defineComponent({ render });

    return {
        // data
        isOpen: readonly(isOpen),
        state: readonly(modal),
        selectedNotifyId,
        authCode,

        // methods
        openNotifyDialog,
        closeDialogWithVerify,
        closeDialog,
        sendNotify,

        // components
        NotifyDialog
    };
}